// src/receptionist/nav.ts
//
// Router-aware navigation for the Receptionist product, following the exact
// shape of @oura/nav so the two products stay legible side by side. The rail
// itself is the shared 80px icon rail (components/mini-sidebar_v2.tsx), grouped
// with thin dividers — this module only decides what goes in it.

import type { LucideIcon } from "lucide-react";
import {
    LayoutDashboardIcon,
    LayoutGridIcon,
    CalendarCheckIcon,
    MessageSquareTextIcon,
    ListChecksIcon,
    SlidersHorizontalIcon,
    ClipboardCheckIcon,
} from "lucide-react";
import type { ReceptionistRole } from "./role";

export type View =
    | "home"           // the Overview: the merchant's landing page (#4847)
    | "bookings"       // the queue: every request with its conversation
    | "flow"           // opener, booking steps and follow-ups
    | "knowledge"      // services, prices, policies, FAQ notes
    | "assistant"      // tone, reminders, escalation rules, after hours
    | "learn"          // upload past chats, review what the AI infers
    | "profile"        // business details, hours, WhatsApp number
    | "setup"          // every set-up step in one menu (#4796): business, WhatsApp, team, sign-off, templates, learn
    | "staff"          // staff channel, alerts, people
    | "overview"       // ourteam-staff roll-up across merchants
    | "settings";      // the product's own settings page, from the user menu

export const RECEPTIONIST_ROOT = "/receptionist";

// The product's OWN settings page (#3614), reached from the avatar menu rather
// than the rail. Not the shared /setting route: that page picks its layout
// from the Oura and Training roles and falls through to the recruiter
// sidebar for everyone else, and extending it would mean editing a live
// product's tab logic.
export const RECEPTIONIST_SETTINGS_PATH = "/receptionist/settings";

export interface ReceptionistNavItem {
    view: View;
    label: string;
    icon: LucideIcon;
    to: string;
}
export interface ReceptionistNavGroup {
    label: string;
    items: ReceptionistNavItem[];
}

export function receptionistHref(
    view: View,
    ctx: { role: ReceptionistRole; merchantId?: string | null },
): string {
    if (view === "overview") return `${RECEPTIONIST_ROOT}/merchants`;

    // ourteam staff operate on a merchant reached from the list, so the
    // single-merchant views nest under that id. Same rule as Oura.
    if (ctx.role === "receptionist-staff" && ctx.merchantId) {
        return `${RECEPTIONIST_ROOT}/merchants/${ctx.merchantId}/${view}`;
    }
    return `${RECEPTIONIST_ROOT}/${view}`;
}

/** The single-merchant tabs: the whole merchant nav, and staff's drilled-in nav. */
function merchantGroups(
    role: ReceptionistRole,
    merchantId?: string | null,
): ReceptionistNavGroup[] {
    const href = (v: View) => receptionistHref(v, { role, merchantId });
    return [
        {
            label: "Front desk",
            items: [
                // The landing page (#4847): how the receptionist is doing, and what needs the team.
                { view: "home", label: "Overview", icon: LayoutGridIcon, to: href("home") },
                { view: "bookings", label: "Bookings", icon: CalendarCheckIcon, to: href("bookings") },
            ],
        },
        {
            // What the AI says and does. Ordered the way a merchant sets it up:
            // teach it the business, then shape the conversation, then tune it.
            label: "Your receptionist",
            items: [
                { view: "knowledge", label: "Services", icon: ListChecksIcon, to: href("knowledge") },
                { view: "flow", label: "Conversation", icon: MessageSquareTextIcon, to: href("flow") },
                { view: "assistant", label: "Behaviour", icon: SlidersHorizontalIcon, to: href("assistant") },
            ],
        },
        {
            // One menu, as Oura's To do → Setup (#4796). Business, Staff and
            // Learn are steps on it; their old addresses open those steps.
            label: "Setup",
            items: [
                { view: "setup", label: "Setup", icon: ClipboardCheckIcon, to: href("setup") },
            ],
        },
    ];
}

export function receptionistNavFor(role: ReceptionistRole): ReceptionistNavGroup[] {
    if (role === "receptionist-client") return merchantGroups(role);
    // ourteam staff land on the merchant list; the per-merchant views are page
    // tabs inside the merchant detail screen, so the rail does not swap on
    // drill-in (the lesson recorded in @oura/nav).
    return [
        {
            label: "Growth",
            items: [
                { view: "overview", label: "Merchants", icon: LayoutDashboardIcon, to: receptionistHref("overview", { role }) },
            ],
        },
    ];
}
